import axios from 'axios'
import router from '@/router'
import { isValidToken, isTokenExpiringSoon } from '@/utils/auth'

// Initial state
const state = {
  token: localStorage.getItem('token') || null,
  user: JSON.parse(localStorage.getItem('user')) || null,
  loading: false,
  error: null,
  validationErrors: {},
  refreshTokenPromise: null
}

// Getters
const getters = {
  isLoggedIn: state => !!state.token,
  currentUser: state => state.user,
  authError: state => state.error,
  isLoading: state => state.loading
}

// Actions
const actions = {
  // Login action
  async login({ commit, dispatch }, credentials) {
    commit('AUTH_REQUEST')
    try {
      // Make sure we have no token when attempting login
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      delete axios.defaults.headers.common['Authorization']

      // Use a direct axios instance without interceptors for login
      const axiosNoInterceptors = axios.create();
      axiosNoInterceptors.interceptors.request.handlers = [];
      axiosNoInterceptors.interceptors.response.handlers = [];

      const response = await axiosNoInterceptors.post('/api/auth/login', credentials)
      const token = response.data.access_token

      // Save token to localStorage
      localStorage.setItem('token', token)
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

      // Get user information
      await dispatch('getUser')

      commit('AUTH_SUCCESS', token)
      return response
    } catch (error) {
      // Clear any existing token to prevent refresh attempts
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      delete axios.defaults.headers.common['Authorization']

      // Determine error message from response
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Authentication failed'

      commit('AUTH_ERROR', errorMessage)
      throw error
    }
  },

  // Register a new user
  async register({ commit, dispatch }, userData) {
    commit('AUTH_REQUEST')

    try {
      // Make sure we have no token when attempting registration
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      delete axios.defaults.headers.common['Authorization']

      // Use a direct axios instance without interceptors for registration
      const axiosNoInterceptors = axios.create();
      axiosNoInterceptors.interceptors.request.handlers = [];
      axiosNoInterceptors.interceptors.response.handlers = [];

      const response = await axiosNoInterceptors.post('/api/auth/register', userData)
      const token = response.data.access_token

      // Save token to localStorage
      localStorage.setItem('token', token)
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

      // Get user information
      await dispatch('getUser')

      commit('AUTH_SUCCESS', token)
      return response
    } catch (error) {
      // Clear any existing token to prevent refresh attempts
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      delete axios.defaults.headers.common['Authorization']

      // Handle validation errors
      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors
        commit('SET_VALIDATION_ERRORS', validationErrors)
        
        // Set a generic error message for the notification
        const errorMessage = 'Please check the form for validation errors'
        commit('AUTH_ERROR', errorMessage)
      } else {
        // Handle other types of errors
        const errorMessage = error.response?.data?.error || 
                            error.response?.data?.message || 
                            'Registration failed'
        commit('AUTH_ERROR', errorMessage)
        commit('CLEAR_VALIDATION_ERRORS')
      }

      throw error
    }
  },

  // Get current user
  async getUser({ commit }) {
    try {
      const response = await axios.get('/api/auth/me')

      // Process user data to ensure we have permissions information
      const userData = response.data;

      // Ensure we have roles and permissions data even if API doesn't provide it
      if (!userData.roles) userData.roles = [];
      if (!userData.permissions) {
        // Extract permissions from roles if possible
        userData.permissions = [];
        if (userData.roles && Array.isArray(userData.roles)) {
          userData.roles.forEach(role => {
            if (role.permissions && Array.isArray(role.permissions)) {
              // Add unique permissions
              role.permissions.forEach(permission => {
                if (!userData.permissions.includes(permission)) {
                  userData.permissions.push(permission);
                }
              });
            }
          });
        }
      }

      commit('SET_USER', userData)
      return response
    } catch (error) {
      console.error('Failed to get user info:', error)
      throw error
    }
  },

    // Initialize auth state on app load
    async initAuth({ dispatch, state }) {
      // Check if we have a token
      const token = localStorage.getItem('token')

      if (!token) return false

      try {
        // If we have a token but no user, fetch user data
        if (!state.user) {
          await dispatch('getUser')
        }
        return true
      } catch (error) {
        // If token is invalid, clear auth state
        dispatch('logout')
        return false
      }
  },

  // Logout action
  async logout({ commit, state }, { silent = false } = {}) {
    // Get the current token before clearing it
    const currentToken = state.token || localStorage.getItem('token')

    // Then attempt to notify the server first (while we still have the token)
    try {
      if (currentToken) {
        // Use a direct axios instance without interceptors to prevent loop
        const axiosNoInterceptors = axios.create();

        // Add a timeout to prevent hanging requests
        const controller = new AbortController();
        setTimeout(() => controller.abort(), 3000);

        await axiosNoInterceptors.post('/api/auth/logout', {}, {
          headers: {
            'Authorization': `Bearer ${currentToken}`
          },
          signal: controller.signal
        });
      }
    } catch (error) {
      // We expect this might fail, so just log it
      console.error('Logout error:', error)
    }

    // Now clear local storage and auth headers
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('lastRefreshAttempt')
    delete axios.defaults.headers.common['Authorization']

    // Set the store state to logged out
    commit('LOGOUT')

    // Only redirect to login page if not silent mode
    if (!silent) {
      router.push('/login')
    }
  },

  // Refresh token
      async refreshToken({ commit, state, dispatch }) {
    // If we're not logged in or no token exists, don't try to refresh
    if (!state.token || !localStorage.getItem('token')) {
      commit('SET_REFRESH_PROMISE', null);
      throw new Error('No token to refresh');
    }

    // Add a safeguard: check if token expired very recently (within last 5 seconds)
    // This helps prevent refresh loops if backend has issues
    const lastRefreshAttempt = localStorage.getItem('lastRefreshAttempt');
    const now = Date.now();
    if (lastRefreshAttempt && (now - parseInt(lastRefreshAttempt)) < 5000) {
      console.warn('Preventing token refresh loop - attempted too recently');
      commit('SET_REFRESH_PROMISE', null);

      // Clear auth data but don't redirect
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('lastRefreshAttempt');
      delete axios.defaults.headers.common['Authorization'];
      commit('LOGOUT');

      throw new Error('Token refresh rate limited');
    }

    // Record this attempt
    localStorage.setItem('lastRefreshAttempt', now.toString());

    try {
      // Use a direct axios instance without interceptors to prevent loop
      const axiosNoInterceptors = axios.create({
        baseURL: axios.defaults.baseURL || '/',
        timeout: 8000, // Add timeout to prevent hanging requests
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      // Get current token to use in refresh request
      const currentToken = localStorage.getItem('token');
      if (!currentToken) {
        throw new Error('No token to refresh');
      }

      // Add authorization header
      axiosNoInterceptors.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;

      // Make the refresh request with abort controller for extra safety
      const controller = new AbortController();
      setTimeout(() => controller.abort(), 7000); // Abort slightly before the timeout

      const response = await axiosNoInterceptors.post('/api/auth/refresh', {}, {
        signal: controller.signal
      });

      // Validate response
      if (!response.data || !response.data.access_token) {
        throw new Error('Invalid token response from server');
      }

      const token = response.data.access_token;

      // Save the new token
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      commit('AUTH_SUCCESS', token);

      // If we had user data before, make sure it's still valid with new token
      if (state.user) {
        try {
          // Attempt to refresh user data silently
          dispatch('getUser').catch(error => {
            console.warn('Failed to refresh user data after token refresh:', error);
            // If user data refresh fails with 401, redirect to login
            if (error.response && error.response.status === 401) {
              router.push('/login');
            }
          });
        } catch (userError) {
          // Just log it, don't fail the token refresh
          console.warn('Error refreshing user data after token refresh:', userError);
        }
      }

      return token;
    } catch (error) {
      console.error('Token refresh failed:', error);

      // Clean up the state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('lastRefreshAttempt');
      delete axios.defaults.headers.common['Authorization'];
      commit('LOGOUT');

      // Redirect to login page
      router.push('/login');

      throw error;
    }
  },

  // Check if token is valid or needs refreshing
  checkToken({ state, dispatch }) {
      if (!state.token) {
        // No token, redirect to login
        router.push('/login');
        return false;
      }

      // If token is invalid, logout and redirect
      if (!isValidToken(state.token)) {
        dispatch('logout');
        return false;
      }

      // If token is about to expire, refresh it
      if (isTokenExpiringSoon(state.token, 5)) {
        return dispatch('refreshToken')
          .then(() => true)
          .catch(() => {
            // If refresh fails, redirect to login
            router.push('/login');
            return false;
          });
      }

      return true;
  },

  // Clear authentication errors
  clearAuthError({ commit }) {
    commit('CLEAR_AUTH_ERROR')
    commit('CLEAR_VALIDATION_ERRORS')
  }
}

// Mutations
const mutations = {
  AUTH_REQUEST(state) {
    state.loading = true
    state.error = null
  },
  AUTH_SUCCESS(state, token) {
    state.token = token
    state.loading = false
    state.error = null
  },
  AUTH_ERROR(state, error) {
    state.error = error
    state.loading = false
  },
  SET_USER(state, user) {
    state.user = user
    if (user) {
      localStorage.setItem('user', JSON.stringify(user))
    } else {
      localStorage.removeItem('user')
    }
  },
  LOGOUT(state) {
    state.token = null
    state.user = null
    state.refreshTokenPromise = null
  },
  CLEAR_AUTH_ERROR(state) {
    state.error = null
  },
  SET_VALIDATION_ERRORS(state, errors) {
    state.validationErrors = errors
  },
  CLEAR_VALIDATION_ERRORS(state) {
    state.validationErrors = {}
  },
  SET_REFRESH_PROMISE(state, promise) {
    state.refreshTokenPromise = promise
  }
}

// Note: Axios interceptors for token refresh are now handled in plugins/axios.js

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
